import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where } from "firebase/firestore";
import ClientEditModal from '../components/Clients/ClientEditModal';

const IconPlus = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"></path></svg>;
const IconPencil = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z"></path><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd"></path></svg>;
const IconTrash = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd"></path></svg>;

const ClientsView = ({ db, user }) => {
    const [clients, setClients] = useState([]);
    const [sellers, setSellers] = useState([]); // <-- Estado para la lista de vendedores
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sellerFilter, setSellerFilter] = useState('all');
    const [clientToEdit, setClientToEdit] = useState(null); // Estado para controlar el modal

    const canManageClients = user.permissions?.canManageClients;
    const isAdmin = user.permissions?.canManageRoles;

    useEffect(() => {
        setIsLoading(true);
        // Cargar Clientes
        const clientsUnsubscribe = onSnapshot(collection(db, "clients"), (snapshot) => {
            const clientsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setClients(clientsData.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
            setIsLoading(false);
        });

        // Cargar Vendedores
        const sellersQuery = query(collection(db, "users"), where("roles", "array-contains", "VENDEDOR"));
        const sellersUnsubscribe = onSnapshot(sellersQuery, (snapshot) => {
            const sellersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSellers(sellersData);
        });
        
        return () => {
            clientsUnsubscribe();
            sellersUnsubscribe();
        };
    }, [db]);
    
    const uniqueSellers = useMemo(() => ([...new Map(clients.filter(c => c.sellerId && c.sellerName).map(c => [c.sellerId, {id: c.sellerId, name: c.sellerName}])).values()]), [clients]);

    const filteredClients = useMemo(() => {
        return clients.filter(client => {
            const matchesSearch = (client.name || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesSeller = (sellerFilter === 'all') || (client.sellerId === sellerFilter);
            return matchesSearch && matchesSeller;
        });
    }, [clients, searchTerm, sellerFilter]);

    const handleSaveClient = async (clientData) => {
        try {
            if (clientToEdit && clientToEdit.id) {
                const clientRef = doc(db, "clients", clientToEdit.id);
                await updateDoc(clientRef, clientData);
            } else {
                await addDoc(collection(db, "clients"), { ...clientData, createdBy: user.uid, createdAt: serverTimestamp() });
            }
        } catch (error) {
            console.error("Error guardando cliente:", error);
        } finally {
            setClientToEdit(null);
        }
    };
    
    const handleDeleteClient = async (clientId) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar este cliente?")) {
            await deleteDoc(doc(db, "clients", clientId));
        }
    };

    return (
        <div className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <h1 className="text-3xl font-bold text-gray-800">Gestión de Clientes</h1>
                <div className="w-full md:w-auto flex flex-wrap gap-4">
                    <input type="text" placeholder="Buscar cliente..." className="w-full sm:w-auto md:w-48 p-2 border border-gray-300 rounded-md" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    {isAdmin && (
                        <select value={sellerFilter} onChange={(e) => setSellerFilter(e.target.value)} className="w-full sm:w-auto md:w-48 p-2 border bg-white border-gray-300 rounded-md">
                            <option value="all">Todos los Vendedores</option>
                            {uniqueSellers.map(seller => (<option key={seller.id} value={seller.id}>{seller.name}</option>))}
                        </select>
                    )}
                    {canManageClients && (
                        <button onClick={() => setClientToEdit({})} className="flex items-center gap-2 px-4 py-2 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                            <IconPlus /> Nuevo
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {isLoading ? (<div className="text-center p-10">Cargando clientes...</div>) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-600">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3">Cliente</th>
                                    <th scope="col" className="px-6 py-3">Contacto Principal</th>
                                    <th scope="col" className="px-6 py-3 text-center">Vendedor Asignado</th>
                                    {canManageClients && <th scope="col" className="px-6 py-3 text-center">Acciones</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClients.map(client => {
                                    const mainContact = client.contacts?.[0];
                                    return (
                                        <tr key={client.id} className="bg-white border-b hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium text-gray-900">{client.name}</td>
                                            <td className="px-6 py-4"><div>{mainContact?.name || 'N/A'}</div><div className="text-xs text-gray-500">{mainContact?.email}</div></td>
                                            <td className="px-6 py-4 text-center">{client.sellerName || 'N/A'}</td>
                                            {canManageClients && (
                                                <td className="px-6 py-4">
                                                    <div className="flex justify-center items-center gap-4">
                                                        <button onClick={() => setClientToEdit(client)} title="Editar" className="text-gray-500 hover:text-blue-600"><IconPencil /></button>
                                                        <button onClick={() => handleDeleteClient(client.id)} title="Eliminar" className="text-gray-500 hover:text-red-600"><IconTrash /></button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {clientToEdit && (
                <ClientEditModal 
                    clientData={clientToEdit}
                    sellers={sellers} // <-- Pasamos la lista de vendedores
                    onSave={handleSaveClient}
                    onClose={() => setClientToEdit(null)}
                />
            )}
        </div>
    );
};

export default ClientsView;

